// src/lib/backend-signed-urls.ts
// Simple service to get signed URLs from backend instead of frontend logic

import { supabase } from './supabase';
import { ACCOUNT_STATUS } from './account-status';
import type { AudioFormat, AudioVariant, SignedAudioResponse } from '@/types/audio-file';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;

/**
 * Gets authentication headers for API calls
 */
async function getAuthHeaders(): Promise<HeadersInit> {
  const { data: { session }, error } = await supabase.auth.getSession();
  if (error) {
    console.error('Error getting session:', error);
    return { 'Content-Type': 'application/json' };
  }

  if (!session?.access_token) {
    console.warn('No auth token found, proceeding without Authorization header.');
    return { 'Content-Type': 'application/json' };
  }

  return {
    'Authorization': `Bearer ${session.access_token}`,
    'Content-Type': 'application/json',
  };
}

/**
 * Options for getting a signed audio URL.
 */
export interface SignedAudioOptions {
  /** Audio format (mp3, wav, flac, etc.) */
  format?: AudioFormat;
  /** Audio variant (full, preview, stem_*, spatial) */
  variant?: AudioVariant;
  /** Legacy quality parameter - maps wav to format=wav */
  quality?: "mp3" | "wav";
  /**
   * Track kind: 'catalog' (default), 'reference' (disco-import reference tracks), or
   * 'project_share' (a catalog track played through a project review link, no login).
   * When 'reference', the backend uses the reference-audio bucket and the share_token
   * for service-role access. Must be included in the cache key to prevent collisions
   * between catalog and reference URLs for the same UUID.
   */
  kind?: "catalog" | "reference" | "project_share";
  /**
   * Share token for reference-track public/share-link access.
   * Required when kind='reference' on a shared playlist view (no user auth).
   */
  shareToken?: string;
}

/**
 * Gets a signed URL for a track's audio file from the backend.
 * Supports multi-format audio via the track_audio_files table.
 *
 * @param trackId The track ID
 * @param options Format/variant options or legacy quality string
 * @returns Promise<string | null> The signed URL or null if error
 */
export async function getSignedAudioUrl(
  trackId: string,
  options: SignedAudioOptions | "mp3" | "wav" = "mp3"
): Promise<string | null> {
  try {
    const response = await fetchSignedAudio(trackId, options);

    if (!response.ok) {
      console.error('Failed to get signed URL:', response.status, response.statusText);
      return null;
    }

    const data: SignedAudioResponse = await response.json();
    return data.url || null;
  } catch (error) {
    console.error('Error getting signed URL from backend:', error);
    return null;
  }
}

/**
 * The backend refusing the ACCOUNT, not the track (N12): `account_incomplete` (a sign-up that was
 * never finished) or `account_access_blocked` (pending approval, rejected, suspended). The signed-in
 * audio path is for fully set-up accounts only; everyone else hears the public preview.
 *
 * `account_pending_approval` is this file's name for an `account_access_blocked` refusal whose
 * `account_status` is `pending_approval` (the backend sends both): that account is waiting, not
 * refused, and is told so (review Q8-14).
 */
export type AccountRefusal = "account_incomplete" | "account_access_blocked" | "account_pending_approval";

export class AccountRefusedError extends Error {
  constructor(readonly reason: AccountRefusal) {
    super(`Signed audio refused: ${reason}`);
    this.name = "AccountRefusedError";
  }
}

async function accountRefusalOf(response: Response): Promise<AccountRefusal | null> {
  if (response.status !== 403) return null;
  try {
    const body = (await response.json()) as { detail?: { reason?: unknown; account_status?: unknown } };
    const reason = body?.detail?.reason;
    if (reason === "account_incomplete") return reason;
    if (reason !== "account_access_blocked") return null;
    return body.detail?.account_status === ACCOUNT_STATUS.PENDING_APPROVAL ? "account_pending_approval" : reason;
  } catch {
    return null;
  }
}

/**
 * A signed URL to download a track's file. Unlike `getSignedAudioUrl` it says WHY when the backend
 * refuses the account (throws `AccountRefusedError`), so the download can tell the person in a
 * sentence instead of "Download failed". Not cached: a download is one deliberate click.
 */
export async function getSignedDownloadUrl(
  trackId: string,
  quality: "mp3" | "wav"
): Promise<string | null> {
  const response = await fetchSignedAudio(trackId, quality);
  if (!response.ok) {
    const refusal = await accountRefusalOf(response);
    if (refusal) throw new AccountRefusedError(refusal);
    console.error('Failed to get signed URL:', response.status, response.statusText);
    return null;
  }
  const data: SignedAudioResponse = await response.json();
  return data.url || null;
}

/** GET /api/tracks/signed-audio with the session, for both readers above. */
async function fetchSignedAudio(
  trackId: string,
  options: SignedAudioOptions | "mp3" | "wav"
): Promise<Response> {
  const authHeaders = await getAuthHeaders();

  // Build query params
  const params = new URLSearchParams({ track_id: trackId });

  // Handle both old string format and new options object
  if (typeof options === 'string') {
    // Legacy: quality string
    params.set('quality', options);
  } else {
    // New: options object
    if (options.quality) {
      params.set('quality', options.quality);
    }
    if (options.format) {
      params.set('format', options.format);
    }
    if (options.variant) {
      params.set('variant', options.variant);
    }
    // Disco-import reference-track threading
    if (options.kind) {
      params.set('kind', options.kind);
    }
    if (options.shareToken) {
      params.set('share_token', options.shareToken);
    }
  }

  return fetch(`${API_BASE_URL}/api/tracks/signed-audio?${params.toString()}`, {
    method: 'GET',
    headers: authHeaders,
  });
}

/**
 * Gets a signed URL with full response metadata.
 * Returns format and variant info along with the URL.
 *
 * @param trackId The track ID
 * @param options Format/variant options
 * @returns Promise<SignedAudioResponse | null>
 */
export async function getSignedAudioUrlWithMetadata(
  trackId: string,
  options: SignedAudioOptions = {}
): Promise<SignedAudioResponse | null> {
  try {
    const authHeaders = await getAuthHeaders();

    const params = new URLSearchParams({ track_id: trackId });
    if (options.format) params.set('format', options.format);
    if (options.variant) params.set('variant', options.variant);
    if (options.quality) params.set('quality', options.quality);

    const response = await fetch(`${API_BASE_URL}/api/tracks/signed-audio?${params.toString()}`, {
      method: 'GET',
      headers: authHeaders,
    });

    if (!response.ok) {
      console.error('Failed to get signed URL:', response.status, response.statusText);
      return null;
    }

    return await response.json();
  } catch (error) {
    console.error('Error getting signed URL from backend:', error);
    return null;
  }
}

/**
 * Gets signed URLs for multiple tracks
 * @param trackIds Array of track IDs
 * @param options Format/variant options (applied to all tracks)
 * @returns Promise<Record<string, string | null>> Map of track ID to signed URL
 */
export async function getSignedAudioUrls(
  trackIds: string[],
  options: SignedAudioOptions = {}
): Promise<Record<string, string | null>> {
  const results: Record<string, string | null> = {};

  // Process all requests in parallel
  const promises = trackIds.map(async (trackId) => {
    const signedUrl = await getSignedAudioUrl(trackId, options);
    results[trackId] = signedUrl;
  });

  await Promise.all(promises);
  return results;
}

/**
 * Cache for signed URLs to avoid unnecessary backend calls
 */
const signedUrlCache = new Map<string, { url: string; expiresAt: number }>();

/**
 * Builds a cache key from track ID and options.
 * MUST include kind + shareToken to avoid collisions between catalog and reference
 * tracks that share the same UUID (C17).
 *
 * Exported for tests so the test suite verifies the real implementation
 * rather than a locally-duplicated copy.
 */
export function buildCacheKey(trackId: string, options: SignedAudioOptions | "mp3" | "wav"): string {
  if (typeof options === 'string') {
    // Legacy string path: treat as catalog, no share token
    return `${trackId}-${options}-full-catalog-`;
  }
  const format = options.format || options.quality || 'mp3';
  const variant = options.variant || 'full';
  const kind = options.kind || 'catalog';
  const shareToken = options.shareToken || '';
  return `${trackId}-${format}-${variant}-${kind}-${shareToken}`;
}

/**
 * Gets a cached signed URL or fetches from backend.
 * Supports multi-format audio via the track_audio_files table.
 *
 * @param trackId The track ID
 * @param options Format/variant options or legacy quality string
 * @returns Promise<string | null> The signed URL or null if error
 */
export async function getCachedSignedAudioUrl(
  trackId: string,
  options: SignedAudioOptions | "mp3" | "wav" = "mp3"
): Promise<string | null> {
  const cacheKey = buildCacheKey(trackId, options);
  const cached = signedUrlCache.get(cacheKey);

  // Check if we have a valid cached URL (with 5 minute buffer)
  if (cached && cached.expiresAt > Date.now()) {
    return cached.url;
  }

  // Fetch new signed URL from backend
  const signedUrl = await getSignedAudioUrl(trackId, options);

  if (signedUrl) {
    // Cache the URL with 15 minute expiration (backend creates 15 min URLs)
    signedUrlCache.set(cacheKey, {
      url: signedUrl,
      expiresAt: Date.now() + (15 * 60 * 1000) - (5 * 60 * 1000) // 10 minutes cache
    });
  }

  return signedUrl;
}

/**
 * Clears the signed URL cache
 */
export function clearSignedUrlCache(): void {
  signedUrlCache.clear();
}
